/**
 * Hat generator — parametric hat with two variants.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   X / Y = horizontal extent (hat centred at origin, +Y is the front/visor side)
 *   Z     = height (brim bottom at Z = 0 for High-Top; crown base at Z = 0 for Snapback)
 *
 * Real-world reference dimensions used as defaults:
 *
 *   High-Top (stovepipe top hat)
 *     Crown: straight cylinder, Ø ≈ 18.5 cm, height 16 cm
 *     Brim:  flat annulus, outer Ø ≈ 38 cm, 3 mm thick
 *     Band:  15 mm ribbon at crown base
 *
 *   Snapback (structured 6-panel cap)
 *     Crown: near-cylindrical with ~12 % inward taper to the button seam,
 *            Ø ≈ 18.5 cm at base, height ≈ 12 cm
 *     Visor: curved plate sector, ~7 cm radial depth × 110° arc × 3 mm thick,
 *            pivoted downward ~12° about its inner edge so the front droops
 *     Button: ~14 mm Ø cylindrical ornament at the crown apex
 *
 * Both crowns are built with `buildLatheGeometry` using a 3-point profile that
 * ends with a pole at the top (same Z as the top ring), so the lathe closes
 * with a clean flat-top fan instead of leaving a rim annulus + open cap. The
 * base ring has no pole, so the bottom remains open as the head opening.
 *
 * The snapback visor is a flat die-cut plate built with `buildExtrudedPolygon`:
 * a 2D D-shape outline (concave back arc hugging the crown, straight sides,
 * convex front arc) is extruded along +Z into a 3 mm plate, then pivoted
 * about its inner edge (the line parallel to X through (0, headR, 0)) so the
 * front edge droops forward at `visorAngle` without lifting the attachment
 * seam off the crown. The back arc traces the crown circle exactly, so the
 * entire seam sits flush against the cylindrical wall.
 *
 * The high-top brim uses `buildRingDisc` for a true closed annular disc, and
 * the band uses `buildCylinder`.
 */

import type { Point2 } from "./generatorGeom.ts";
import {
  buildCylinder,
  buildExtrudedPolygon,
  buildLatheGeometry,
  buildRingDisc,
  concatPrimitiveGeometries,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  defineGenerator,
  enumParam,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface HatParams extends GeneratorParams {
  style: string;
  headDiameter: number;
  crownHeight: number;
  brimDiameter: number;
  brimThickness: number;
  bandHeight: number;
  visorLength: number;
  visorAngle: number;
  segments: number;
  loops: number;
}

const HAT_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "style",
    label: "Style",
    type: "enum",
    default: "high_top",
    options: [
      { value: "high_top", label: "High-Top" },
      { value: "snapback", label: "Snapback" },
    ],
  },
  {
    type: "section",
    id: "hat-fit",
    label: "Fit",
    params: [
      {
        id: "headDiameter",
        label: "Head Diameter",
        type: "number",
        unit: "meters",
        default: 0.185,
        min: 0.14,
        max: 0.24,
        step: 0.005,
        description:
          "Outer diameter of the head opening. Adult average ≈ 0.185 m.",
      },
    ],
  },
  {
    type: "section",
    id: "hat-crown",
    label: "Crown",
    params: [
      {
        id: "crownHeight",
        label: "Crown Height",
        type: "number",
        unit: "meters",
        default: 0.11,
        min: 0.04,
        max: 0.3,
        step: 0.005,
        description: "High-Top: 0.14–0.18 m · Snapback: 0.10–0.13 m",
      },
    ],
  },
  {
    type: "section",
    id: "hat-brim",
    label: "Brim",
    showIf: { id: "style", value: "high_top" },
    params: [
      {
        id: "brimDiameter",
        label: "Brim Diameter",
        type: "number",
        unit: "meters",
        default: 0.38,
        min: 0.2,
        max: 0.7,
        step: 0.01,
        description: "Outer brim diameter. High-Top: 0.35–0.40 m.",
      },
      {
        id: "brimThickness",
        label: "Brim Thickness",
        type: "number",
        unit: "meters",
        default: 0.003,
        min: 0.001,
        max: 0.015,
        step: 0.001,
      },
    ],
  },
  {
    type: "section",
    id: "hat-visor",
    label: "Visor",
    showIf: { id: "style", value: "snapback" },
    params: [
      {
        id: "visorLength",
        label: "Visor Length",
        type: "number",
        unit: "meters",
        default: 0.07,
        min: 0.04,
        max: 0.12,
        step: 0.005,
        description:
          "Radial depth of the visor measured from the crown. Structured snapback: 0.065–0.080 m.",
      },
      {
        id: "visorAngle",
        label: "Downward Angle",
        type: "number",
        unit: "degrees",
        default: 12,
        min: 0,
        max: 25,
        step: 1,
        description:
          "Downward tilt of the visor below horizontal. Typical snapbacks: 10–20°.",
      },
    ],
  },
  {
    type: "section",
    id: "hat-details",
    label: "Details",
    showIf: { id: "style", value: "high_top" },
    params: [
      {
        id: "bandHeight",
        label: "Band Height",
        type: "number",
        unit: "meters",
        default: 0.015,
        min: 0.005,
        max: 0.05,
        step: 0.002,
        description: "Height of the decorative ribbon around the crown base.",
      },
    ],
  },
  {
    type: "section",
    id: "hat-resolution",
    label: "Resolution",
    defaultCollapsed: true,
    params: [
      {
        id: "segments",
        label: "Segments",
        type: "number",
        unit: "count",
        default: 32,
        min: 8,
        max: 128,
        step: 4,
      },
      {
        id: "loops",
        label: "Crown Loops",
        type: "number",
        unit: "count",
        default: 0,
        min: 0,
        max: 8,
        step: 1,
        description:
          "Extra horizontal ring cuts in the crown for a smoother silhouette.",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Style-specific geometry constants
// ---------------------------------------------------------------------------

/**
 * Snapback crown top-radius fraction relative to the head (base) radius.
 * A ~12 % inward taper matches real 6-panel construction where the front,
 * side, and rear panels converge toward the button seam at the apex.
 */
const SNAPBACK_TOP_RATIO = 0.88;

/**
 * Snapback visor half-width as a fraction of the head radius. A value of 0.9
 * makes the straight sides of the dee-shape sit 10 % inside the head radius,
 * which gives a visor width ≈ 1.8 · headR — close to the 18 cm / 18.5 cm real
 * snapback ratio without pushing the sides so far back that the rear corners
 * float noticeably off the crown after the droop tilt.
 */
const VISOR_HALF_WIDTH_RATIO = 0.9;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a crown shell as a surface of revolution.
 *
 * The profile rises from `(0, baseR)` through `loops`-many intermediate rings
 * (linearly tapered toward `topR`) to the top ring at `(height, topR)`, then
 * closes with a pole at `(height, 0)`. Because the top ring and pole share
 * the same Z, `buildLatheGeometry` emits a flat triangle fan across the top —
 * a clean closed cap. The base has no pole, leaving the head opening exposed
 * at Z = 0.
 */
function buildCrownLathe(
  baseR: number,
  topR: number,
  height: number,
  loops: number,
  segments: number,
) {
  const steps = Math.max(1, loops + 1);
  const points: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    points.push([height * t, baseR + (topR - baseR) * t]);
  }
  points.push([height, 0]); // pole → flat top cap
  return buildLatheGeometry(points, segments);
}

/**
 * Build the 2D outline of a snapback visor as a D-shape.
 *
 *   • Front (convex) arc of radius `outerR`, forms the top of the dee.
 *     Traversed right → left: x sweeps +W → −W, y = √(outerR² − x²).
 *   • Left side at x = −W (implicit edge from (−W, yo_left) to (−W, yi_left)).
 *   • Back (concave) arc of radius `innerR`, forms the bottom of the dee.
 *     Traversed left → right: x sweeps −W → +W, y = √(innerR² − x²). This
 *     traces the crown circle so the visor's rear seam sits flush against
 *     the cylindrical wall.
 *   • Right side at x = +W (implicit, closes the loop).
 *
 * Point order is CCW when viewed from +Z, matching the winding that
 * `buildExtrudedPolygon` expects for the outer contour (though the extruder
 * auto-normalises winding, so callers never have to trust this detail).
 */
function buildVisorOutline(
  innerR: number,
  outerR: number,
  halfWidth: number,
  segments: number,
): Point2[] {
  const N = Math.max(4, Math.round(segments));
  const W = Math.min(innerR - 1e-4, Math.max(1e-4, halfWidth));
  const pts: Point2[] = [];

  // Outer arc — right to left (x: +W → −W). Both endpoints included.
  for (let i = 0; i <= N; i++) {
    const x = W - (2 * W * i) / N;
    pts.push([x, Math.sqrt(outerR * outerR - x * x)]);
  }
  // Inner arc — left to right (x: −W → +W). Both endpoints included. The
  // implicit polygon edge between the last outer point (−W, yo_left) and
  // the first inner point (−W, yi_left) forms the vertical left side at
  // x = −W; the loop-closing edge between the last inner point (+W, yi_right)
  // and the first outer point (+W, yo_right) forms the right side.
  for (let i = 0; i <= N; i++) {
    const x = -W + (2 * W * i) / N;
    pts.push([x, Math.sqrt(innerR * innerR - x * x)]);
  }
  return pts;
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildHat(p: HatParams, _ctx: GeneratorBuildContext): GeneratorOutput {
  const style = enumParam(p, "style", "high_top");
  const headD = numParam(p, "headDiameter", 0.185);
  const crownH = numParam(p, "crownHeight", 0.12);
  const brimD = numParam(p, "brimDiameter", 0.38);
  const brimT = numParam(p, "brimThickness", 0.003);
  const bandH = numParam(p, "bandHeight", 0.015);
  const visorL = numParam(p, "visorLength", 0.07);
  const visorA = numParam(p, "visorAngle", 12);
  const segs = intParam(p, "segments", 32);
  const loops = intParam(p, "loops", 0);

  const headR = headD / 2;
  const parts = [];

  // ── Snapback ───────────────────────────────────────────────────────────────
  if (style === "snapback") {
    //
    // Crown — structured 6-panel silhouette.
    //
    // Linear-walled lathe with a slight inward taper toward the button seam.
    // This is the critical contrast with a curved baseball cap: a convex
    // profile bulges outward in the middle, producing the "bloated" cartoon
    // look the design brief called out. Straight walls read as the clean,
    // architectural silhouette of a real structured snapback.
    //
    const crownLoops = Math.max(loops, 4);
    parts.push(
      buildCrownLathe(
        headR,
        headR * SNAPBACK_TOP_RATIO,
        crownH,
        crownLoops,
        segs,
      ),
    );

    //
    // Top button — small cylindrical ornament at the crown apex.
    //   Ø ≈ 14 mm, height ≈ 4 mm (real-world snapback button reference).
    //   Positioned so its base sits on the crown's closed top cap.
    //
    const buttonR = 0.007;
    const buttonH = 0.004;
    const button = buildCylinder(
      buttonR,
      buttonH,
      Math.max(Math.round(segs / 2), 12),
    );
    parts.push(transformGeometry(button, [0, 0, crownH + buttonH / 2]));

    //
    // Visor — flat die-cut dee-shape extruded to a 3 mm plate.
    //
    //   Inner arc radius = headR                         → traces the crown wall exactly
    //   Outer arc radius = headR + visorL                → radial depth of the visor
    //   Half-width       = headR × VISOR_HALF_WIDTH_RATIO → straight side cuts
    //   Thickness        = 3 mm                          → rigid brim plate
    //
    // Pivoting: we want the inner edge (the seam against the crown) to stay
    // on the crown while the outer edge tips down by `visorAngle`. Rotating
    // the plate about the X-axis line through (0, headR, 0) achieves this.
    // `transformGeometry` does `p' = R·p + t`, so an equivalent pivot rotation
    // about point `c` uses `t = c − R·c`. With `c = (0, headR, 0)` and
    // `R = rotX(−α)`:
    //
    //     R·c = (0, headR·cos α, −headR·sin α)
    //     t   = c − R·c = (0, headR(1 − cos α), headR·sin α)
    //
    // The inner-edge apex (0, headR, 0) stays exactly on the crown at ground
    // level; because the rest of the rear arc is on the same circle, the
    // whole seam remains within a millimetre of the crown wall after tilt.
    //
    const visorT = 0.003;
    const visorAngleRad = visorA * (Math.PI / 180);
    const visorSegs = Math.max(8, Math.round(segs / 2));
    const visorOutline = buildVisorOutline(
      headR,
      headR + visorL,
      headR * VISOR_HALF_WIDTH_RATIO,
      visorSegs,
    );
    const visor = buildExtrudedPolygon(visorOutline, visorT);
    const ty = headR * (1 - Math.cos(visorAngleRad));
    const tz = headR * Math.sin(visorAngleRad);
    parts.push(transformGeometry(visor, [0, ty, tz], [-visorAngleRad, 0, 0]));

    return { kind: "mesh", geometry: concatPrimitiveGeometries(parts) };
  }

  // ── High-Top ───────────────────────────────────────────────────────────────

  const brimR = Math.max(headR + 0.01, brimD / 2);

  //
  // Brim — flat ring disc (washer), bottom at Z = 0, top at Z = brimT.
  //   innerRadius = headR so the crown passes through and sits flush.
  //
  parts.push(buildRingDisc(brimR, headR, brimT, segs));

  //
  // Crown — straight cylindrical lathe with a closed flat top.
  //   Lifted by `brimT` so its head-opening ring sits on top of the brim.
  //
  const crown = buildCrownLathe(headR, headR, crownH, loops, segs);
  parts.push(transformGeometry(crown, [0, 0, brimT]));

  //
  // Hat band — solid cylinder, 3 mm wider radius than the crown wall.
  //   Positioned at the crown base, just above the brim surface.
  //   buildCylinder centres at origin (±bandH/2 in Z), so translate by
  //   brimT + bandH/2 to place it from Z = brimT to Z = brimT + bandH.
  //
  const bandR = headR + 0.003;
  const band = buildCylinder(bandR, bandH, segs);
  parts.push(transformGeometry(band, [0, 0, brimT + bandH / 2]));

  return { kind: "mesh", geometry: concatPrimitiveGeometries(parts) };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const HAT_GENERATOR: Generator = defineGenerator<HatParams>({
  id: "hat",
  label: "Hat",
  category: "prop",
  paramSpecs: HAT_SPECS,
  defaultParams: paramsFromSpecs(HAT_SPECS) as HatParams,
  build: buildHat,
});
